import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import {
  BookOpen, Users, School, MapPin, Upload, FileText, 
  ArrowLeft, Search, Filter, TrendingUp, Euro, AlertTriangle,
  Download, Trash2, UserPlus, Check, X, Plus, Building
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MultiSchoolAdminPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // Data states
  const [cities, setCities] = useState([]);
  const [schools, setSchools] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [schoolStudents, setSchoolStudents] = useState([]);
  const [schoolClasses, setSchoolClasses] = useState([]);
  const [usageStats, setUsageStats] = useState(null);
  
  // Upload states
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadPreview, setUploadPreview] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Search/Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  
  // New establishment form states
  const [newEtab, setNewEtab] = useState({
    name: "",
    city: "",
    postal_code: "",
    department: "",
    region: "",
    type: "lycee"
  });
  const [creatingEtab, setCreatingEtab] = useState(false);

  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    if (selectedCity) {
      fetchSchools(selectedCity);
    }
  }, [selectedCity]);

  useEffect(() => {
    if (selectedSchool) {
      fetchSchoolData(selectedSchool.id);
    }
  }, [selectedSchool]);

  const fetchCities = async () => {
    try {
      const response = await axios.get(`${API}/etablissements/villes`);
      setCities(response.data.cities || []);
    } catch (error) {
      console.error("Error fetching cities:", error);
      toast.error("Erreur lors du chargement des villes");
    } finally {
      setLoading(false);
    }
  };

  const fetchSchools = async (city) => {
    try {
      const response = await axios.get(`${API}/etablissements/by-city/${encodeURIComponent(city)}`);
      setSchools(response.data.etablissements || []);
    } catch (error) {
      console.error("Error fetching schools:", error);
      toast.error("Erreur lors du chargement des établissements");
    }
  };

  const fetchSchoolData = async (schoolId) => {
    setLoading(true);
    try {
      const [classesRes, usageRes, studentsRes] = await Promise.all([
        axios.get(`${API}/etablissements/${schoolId}/classes`),
        axios.get(`${API}/etablissements/${schoolId}/usage`),
        axios.get(`${API}/students?etablissement_id=${schoolId}`)
      ]);
      
      setSchoolClasses(classesRes.data.classes || []);
      setUsageStats(usageRes.data);
      setSchoolStudents(studentsRes.data || []);
    } catch (error) {
      console.error("Error fetching school data:", error);
      // Usage API might return 404 if no usage yet
      if (error.response?.status !== 404) {
        toast.error("Erreur lors du chargement des données");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
      'application/vnd.ms-excel', // xls
      'text/csv',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    if (!allowedTypes.includes(file.type) && 
        !file.name.endsWith('.xlsx') && 
        !file.name.endsWith('.xls') && 
        !file.name.endsWith('.csv') &&
        !file.name.endsWith('.pdf') &&
        !file.name.endsWith('.doc') &&
        !file.name.endsWith('.docx')) {
      toast.error("Format de fichier non supporté. Utilisez Excel, CSV, Word ou PDF.");
      return;
    }
    
    setUploadFile(file);
    parseFilePreview(file);
  };

  const parseFilePreview = async (file) => {
    // For CSV files, we can parse locally
    if (file.name.endsWith('.csv') || file.type === 'text/csv') {
      const text = await file.text();
      const lines = text.split('\n').filter(l => l.trim());
      const preview = lines.slice(0, 6).map((line, idx) => {
        const parts = line.split(/[,;]/).map(p => p.trim().replace(/"/g, ''));
        if (idx === 0) {
          return { isHeader: true, values: parts };
        }
        return {
          isHeader: false,
          prenom: parts[0] || '',
          nom: parts[1] || '',
          classe: parts[2] || ''
        };
      });
      setUploadPreview(preview);
    } else {
      // For other formats, we'll process on backend
      setUploadPreview([
        { isHeader: true, values: ['Format détecté:', file.name.split('.').pop().toUpperCase()] },
        { isInfo: true, message: "Le fichier sera traité localement et supprimé après import (RGPD)" }
      ]);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !selectedSchool) {
      toast.error("Sélectionnez un fichier et un établissement");
      return;
    }

    setUploading(true);
    setUploadProgress(10);

    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('etablissement_id', selectedSchool.id);

      setUploadProgress(30);
      
      const response = await axios.post(`${API}/students/bulk-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 50) / progressEvent.total) + 30;
          setUploadProgress(progress);
        }
      });

      setUploadProgress(100);
      
      toast.success(`${response.data.imported} élèves importés avec succès !`);
      
      // Refresh data
      fetchSchoolData(selectedSchool.id);
      
      // Reset upload state
      setUploadFile(null);
      setUploadPreview([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'import");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const filteredStudents = schoolStudents.filter(s => {
    const fullName = `${s.first_name} ${s.last_name}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase()) ||
           s.class_name?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const createNewEtablissement = async () => {
    if (!newEtab.name || !newEtab.city || !newEtab.postal_code) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    
    setCreatingEtab(true);
    try {
      const response = await axios.post(`${API}/etablissements/create`, {
        ...newEtab,
        levels: ["terminale"]
      });
      toast.success(`Établissement "${newEtab.name}" créé avec succès !`);
      
      // Reset form
      setNewEtab({
        name: "",
        city: "",
        postal_code: "",
        department: "",
        region: "",
        type: "lycee"
      });
      
      // Refresh cities list
      fetchCities();
      
    } catch (error) {
      console.error("Error creating establishment:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setCreatingEtab(false);
    }
  };

  const renderNewEtablissement = () => (
    <Card data-testid="new-establishment-form">
      <CardHeader>
        <CardTitle className="text-lg text-[#1A365D] flex items-center gap-2">
          <Plus className="w-5 h-5" />
          Ajouter un nouvel établissement
        </CardTitle>
        <CardDescription>
          Créez un nouveau lycée catholique dans le système SophIA
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="etab-name">Nom de l'établissement *</Label>
            <Input
              id="etab-name"
              placeholder="Ex: Lycée Saint-Jean"
              value={newEtab.name}
              onChange={(e) => setNewEtab({...newEtab, name: e.target.value})}
              className="mt-1"
              data-testid="new-etab-name"
            />
          </div>
          <div>
            <Label htmlFor="etab-city">Ville *</Label>
            <Input
              id="etab-city"
              placeholder="Ex: Lyon"
              value={newEtab.city}
              onChange={(e) => setNewEtab({...newEtab, city: e.target.value})}
              className="mt-1"
              data-testid="new-etab-city"
            />
          </div>
        </div>
        
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="etab-postal">Code Postal *</Label>
            <Input
              id="etab-postal"
              placeholder="Ex: 69000"
              value={newEtab.postal_code}
              onChange={(e) => setNewEtab({...newEtab, postal_code: e.target.value})}
              className="mt-1"
              data-testid="new-etab-postal"
            />
          </div>
          <div>
            <Label htmlFor="etab-dept">Département</Label>
            <Input
              id="etab-dept"
              placeholder="Ex: Rhône"
              value={newEtab.department}
              onChange={(e) => setNewEtab({...newEtab, department: e.target.value})}
              className="mt-1"
              data-testid="new-etab-dept"
            />
          </div>
          <div>
            <Label htmlFor="etab-region">Région</Label>
            <Input
              id="etab-region"
              placeholder="Ex: Auvergne-Rhône-Alpes"
              value={newEtab.region}
              onChange={(e) => setNewEtab({...newEtab, region: e.target.value})}
              className="mt-1"
              data-testid="new-etab-region"
            />
          </div>
        </div>
        
        <div>
          <Label htmlFor="etab-type">Type d'établissement</Label>
          <Select 
            value={newEtab.type} 
            onValueChange={(value) => setNewEtab({...newEtab, type: value})}
          >
            <SelectTrigger className="mt-1" data-testid="new-etab-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="lycee">Lycée</SelectItem>
              <SelectItem value="prepa">Classes Préparatoires</SelectItem>
              <SelectItem value="college">Collège</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => setNewEtab({
              name: "",
              city: "",
              postal_code: "",
              department: "",
              region: "",
              type: "lycee"
            })}
          >
            Réinitialiser
          </Button>
          <Button
            onClick={createNewEtablissement}
            disabled={creatingEtab || !newEtab.name || !newEtab.city || !newEtab.postal_code}
            className="bg-[#22C55E] hover:bg-[#16A34A]"
            data-testid="create-etab-btn"
          >
            {creatingEtab ? (
              "Création..."
            ) : (
              <>
                <Plus className="w-4 h-4 mr-1" />
                Créer l'établissement
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderSchoolSelector = () => (
    <Card className="mb-6" data-testid="school-selector">
      <CardHeader>
        <CardTitle className="text-lg text-[#1A365D] flex items-center gap-2">
          <School className="w-5 h-5" />
          Sélection de l'établissement
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Ville</Label>
            <Select value={selectedCity} onValueChange={setSelectedCity}>
              <SelectTrigger className="mt-1" data-testid="city-filter-select">
                <SelectValue placeholder="Choisir une ville" />
              </SelectTrigger>
              <SelectContent>
                {cities.map((city) => (
                  <SelectItem key={city} value={city}>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      {city}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>Établissement</Label>
            <Select 
              value={selectedSchool?.id || ""} 
              onValueChange={(id) => setSelectedSchool(schools.find(s => s.id === id))}
              disabled={!selectedCity}
            >
              <SelectTrigger className="mt-1" data-testid="school-filter-select">
                <SelectValue placeholder="Choisir un établissement" />
              </SelectTrigger>
              <SelectContent>
                {schools.map((school) => (
                  <SelectItem key={school.id} value={school.id}>
                    {school.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {selectedSchool && (
          <div className="mt-4 p-4 bg-[#F8F9FA] rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-[#1A365D]">{selectedSchool.name}</p>
                <p className="text-sm text-gray-500">{selectedSchool.city} ({selectedSchool.postal_code})</p>
              </div>
              <Badge className="bg-[#22C55E]">
                {schoolStudents.length} élèves
              </Badge>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderUsageStats = () => {
    if (!usageStats || !selectedSchool) return null;

    return (
      <Card className="mb-6" data-testid="usage-stats">
        <CardHeader>
          <CardTitle className="text-lg text-[#1A365D] flex items-center gap-2">
            <Euro className="w-5 h-5" />
            Coût d'utilisation de SophIA
          </CardTitle>
          <CardDescription>Statistiques de consommation pour {selectedSchool.name}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-[#F8F9FA] rounded-lg">
              <p className="text-2xl font-bold text-[#1A365D]">
                {(usageStats.total_cost || 0).toFixed(2)}€
              </p>
              <p className="text-xs text-gray-500">Coût total</p>
            </div>
            <div className="text-center p-4 bg-[#F8F9FA] rounded-lg">
              <p className="text-2xl font-bold text-[#D4AF37]">
                {(usageStats.month_cost || 0).toFixed(2)}€
              </p>
              <p className="text-xs text-gray-500">Ce mois</p>
            </div>
            <div className="text-center p-4 bg-[#F8F9FA] rounded-lg">
              <p className="text-2xl font-bold text-[#22C55E]">
                {(usageStats.today_cost || 0).toFixed(4)}€
              </p>
              <p className="text-xs text-gray-500">Aujourd'hui</p>
            </div>
            <div className="text-center p-4 bg-[#F8F9FA] rounded-lg">
              <p className="text-2xl font-bold text-gray-600">
                {usageStats.total_operations || 0}
              </p>
              <p className="text-xs text-gray-500">Opérations</p>
            </div>
          </div>
          
          {usageStats.cost_breakdown && (
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-600 mb-2">Tarifs par opération :</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(usageStats.cost_breakdown).map(([op, cost]) => (
                  <Badge key={op} variant="outline" className="text-xs">
                    {op.replace(/_/g, ' ')}: {cost}€
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderBulkUpload = () => (
    <Card className="mb-6" data-testid="bulk-upload-section">
      <CardHeader>
        <CardTitle className="text-lg text-[#1A365D] flex items-center gap-2">
          <Upload className="w-5 h-5" />
          Import en masse des élèves
        </CardTitle>
        <CardDescription>
          Importez une liste d'élèves depuis Excel, CSV, Word ou PDF. 
          <span className="text-[#22C55E] font-medium"> Traitement local (RGPD)</span>
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!selectedSchool ? (
          <div className="text-center py-8 text-gray-500">
            <School className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Sélectionnez d'abord un établissement</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div 
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#1A365D] transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv,.pdf,.doc,.docx"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-upload-input"
              />
              <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p className="text-sm text-gray-600">
                {uploadFile ? uploadFile.name : "Cliquez pour sélectionner un fichier"}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Formats acceptés : Excel (.xlsx, .xls), CSV, Word (.doc, .docx), PDF
              </p>
            </div>

            {uploadPreview.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-600 mb-2">Aperçu :</p>
                <div className="space-y-1 text-sm">
                  {uploadPreview.map((row, idx) => (
                    <div key={idx} className={`flex gap-4 ${row.isHeader ? 'font-medium text-gray-800' : 'text-gray-600'}`}>
                      {row.isInfo ? (
                        <span className="text-[#22C55E]">{row.message}</span>
                      ) : row.isHeader ? (
                        row.values?.map((v, i) => <span key={i}>{v}</span>)
                      ) : (
                        <>
                          <span>{row.prenom}</span>
                          <span>{row.nom}</span>
                          <span className="text-gray-400">{row.classe}</span>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {uploading && (
              <div className="space-y-2">
                <Progress value={uploadProgress} className="h-2" />
                <p className="text-sm text-center text-gray-500">Import en cours... {uploadProgress}%</p>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                onClick={handleUpload}
                disabled={!uploadFile || uploading}
                className="flex-1 bg-[#1A365D] hover:bg-[#132745]"
                data-testid="upload-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? "Import en cours..." : "Importer les élèves"}
              </Button>
              {uploadFile && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setUploadFile(null);
                    setUploadPreview([]);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>

            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-700">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                <strong>RGPD :</strong> Le fichier est traité localement et immédiatement supprimé après l'import.
                Aucune donnée n'est stockée sur le serveur.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderStudentList = () => (
    <Card data-testid="student-list-section">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg text-[#1A365D] flex items-center gap-2">
            <Users className="w-5 h-5" />
            Liste des élèves
          </CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-48"
                data-testid="student-search-input"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/students/tracking")}
            >
              <TrendingUp className="w-4 h-4 mr-1" />
              Suivi détaillé
            </Button>
          </div>
        </div>
        <CardDescription>
          {selectedSchool ? `${filteredStudents.length} élèves à ${selectedSchool.name}` : "Sélectionnez un établissement"}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!selectedSchool ? (
          <div className="text-center py-8 text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Sélectionnez un établissement pour voir les élèves</p>
          </div>
        ) : (
          <ScrollArea className="h-[400px]">
            <div className="space-y-2">
              {filteredStudents.map((student) => (
                <div
                  key={student.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  data-testid={`student-row-${student.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#1A365D] flex items-center justify-center text-white font-medium">
                      {student.first_name.charAt(0)}{student.last_name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium text-[#1A365D]">
                        {student.first_name} {student.last_name}
                      </p>
                      <p className="text-sm text-gray-500">{student.class_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-medium">{student.points} pts</p>
                      <p className="text-xs text-gray-500">Niveau {student.level}</p>
                    </div>
                    <Badge className={student.password_set ? "bg-[#22C55E]" : "bg-gray-400"}>
                      {student.password_set ? <Check className="w-3 h-3" /> : "En attente"}
                    </Badge>
                  </div>
                </div>
              ))}
              
              {filteredStudents.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Aucun élève trouvé</p>
                </div>
              )}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="multi-school-admin-page">
      {/* Navigation */}
      <nav className="navbar px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/dashboard")}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Retour
          </Button>
          <span className="navbar-logo hidden sm:block">SophIA</span>
          <Badge className="ml-2 bg-[#D4AF37] text-[#0F172A]">Admin Multi-Écoles</Badge>
        </div>
      </nav>

      <main className="px-4 md:px-8 py-6 max-w-7xl mx-auto page-enter">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl text-[#1A365D] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Gestion Multi-Établissements
          </h1>
          <p className="text-gray-500">
            Gérez les élèves de tous les établissements catholiques de France
          </p>
        </div>

        {renderSchoolSelector()}
        {renderUsageStats()}
        
        <Tabs defaultValue="upload" className="space-y-4">
          <TabsList>
            <TabsTrigger value="upload" className="flex items-center gap-2">
              <Upload className="w-4 h-4" />
              Import en masse
            </TabsTrigger>
            <TabsTrigger value="students" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Liste des élèves
            </TabsTrigger>
            <TabsTrigger value="new-etab" className="flex items-center gap-2">
              <Building className="w-4 h-4" />
              Nouvel établissement
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="upload">
            {renderBulkUpload()}
          </TabsContent>
          
          <TabsContent value="students">
            {renderStudentList()}
          </TabsContent>
          
          <TabsContent value="new-etab">
            {renderNewEtablissement()}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default MultiSchoolAdminPage;
